import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser'
import {
  AuthTokenDto,
  OrganizationDTOV2,
  RefreshTokenDtoV3ReturnTypeEnum,
  SignInDtoV3,
  SignInDtoV3ReturnTypeEnum,
  UserEmailDto,
  VerifyAuthenticationDto,
  VerifyAuthenticationDtoReturnTypeEnum,
  VerifyResistrationDto,
  VerifyResistrationDtoReturnTypeEnum,
} from '@workers-benefit-fund/api'

import { FUTURE_BANK } from 'src/default/config'

import { recordExplicitLogin, setAuthInfo } from '../store/auth.store'
import { isCookieAuthenticationEnabled } from '../utils/isCookieAuthenticationEnabled'

import { ApiResponseSerializer } from '@v2/commons/interface/ApiResponseSerializer'
import {
  getExistingBrowserInstallationId,
  getOptionalBrowserInstallationId,
} from '@v2/domains/notification/utils/getBrowserInstallationId'
import { IOrganizationChangeResponse } from '@v2/domains/organization/organization.types'
import { resetOrganizationSwitchSessionAfterLogin } from '@v2/domains/organization/utils/organizationSwitchSession.utils'
import {
  AuthProfileResponse,
  AuthSessionProfileResponse,
  RestorableSessionProfile,
  SessionProfile,
  toRestorableSessionProfile,
  toSessionOrganization,
  toSessionProfile,
} from '@v2/domains/user/session.mapper'
import { setUserSession } from '@v2/domains/user/store/user.store'
import { SessionOrganization } from '@v2/domains/user/user.types'
import { rootApi } from '@v2/store/api/rootApi'

interface ActivePasskeyStatus {
  hasActivePasskey?: boolean
}

export type CookieAuthResponse = Pick<AuthTokenDto, 'userId' | 'hasPasskeys'> &
  ActivePasskeyStatus

export type AuthResponse =
  | (AuthTokenDto & ActivePasskeyStatus)
  | CookieAuthResponse

export interface RefreshAuthRequest {
  refreshToken?: string
  refresh_token?: string
  organizationId?: string
  membershipId?: string
}

export interface SetNewPasswordRequest {
  password: string
  bearerToken?: string
}

export interface PasskeyRegistrationIdentity {
  userId: string
}

interface PasskeyRegistrationTokens extends PasskeyRegistrationIdentity {
  access_token: string
  refresh_token: string
}

export type PasskeyRegistrationAuth =
  | PasskeyRegistrationTokens
  | PasskeyRegistrationIdentity

interface VerifyPasskeyRegistrationRequest
  extends Pick<VerifyResistrationDto, 'accessToken'> {
  registrationResponse: RegistrationResponseJSON
}

interface VerifyPasskeyLoginRequest
  extends Pick<VerifyAuthenticationDto, 'accessToken'> {
  authenticationResponse: AuthenticationResponseJSON
  challenge: string
}

const projectPasskeyAuth = (
  response: unknown,
  cookieMode: boolean,
): PasskeyRegistrationAuth => {
  const data =
    response && typeof response === 'object'
      ? (response as Record<string, unknown>)
      : {}
  const field = (name: keyof AuthTokenDto) =>
    typeof data[name] === 'string' ? (data[name] as string) : ''
  if (cookieMode) return { userId: field('userId') }
  return {
    access_token: field('access_token'),
    refresh_token: field('refresh_token'),
    userId: field('userId'),
  }
}

const safePasskeyError = (error: unknown) => ({
  status:
    error && typeof error === 'object' && 'status' in error
      ? error.status
      : undefined,
})

export const isTokenAuthResponse = (
  response: AuthResponse,
): response is AuthTokenDto => 'access_token' in response

export const prepareUserOrganizations = (
  organizations: OrganizationDTOV2[],
): SessionOrganization[] =>
  [...organizations]
    .filter((organization) => organization?.id !== FUTURE_BANK)
    .sort((firstOrganization, secondOrganization) =>
      (firstOrganization.name ?? '').localeCompare(
        secondOrganization.name ?? '',
      ),
    )
    .map(toSessionOrganization)

export const authApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    passkeyRegistrationOptions: builder.mutation<
      PublicKeyCredentialCreationOptionsJSON,
      string | void
    >({
      query: (accessToken) => ({
        url: '/auth/passkey/register/options',
        method: 'POST',
        data: isCookieAuthenticationEnabled
          ? {}
          : { access_token: accessToken },
      }),
      transformErrorResponse: safePasskeyError,
    }),
    verifyPasskeyRegistration: builder.mutation<
      PasskeyRegistrationAuth,
      VerifyPasskeyRegistrationRequest
    >({
      async queryFn({ accessToken, registrationResponse }, _, __, baseQuery) {
        const cookieMode = isCookieAuthenticationEnabled
        const data: VerifyResistrationDto = cookieMode
          ? {
              returnType: VerifyResistrationDtoReturnTypeEnum.COOKIES,
              registrationResponse,
            }
          : {
              access_token: accessToken,
              accessToken,
              returnType: VerifyResistrationDtoReturnTypeEnum.OBJECT,
              registrationResponse,
            }
        const result = await baseQuery({
          url: '/auth/passkey/register/verify',
          method: 'POST',
          data,
        })
        if (result.error) return { error: safePasskeyError(result.error) }

        const projected = projectPasskeyAuth(result.data, cookieMode)
        if (cookieMode && !projected.userId.trim())
          return { error: { status: 'INVALID_RESPONSE' } }
        return { data: projected }
      },
    }),
    passkeyLoginOptions: builder.mutation<
      PublicKeyCredentialRequestOptionsJSON,
      string | void
    >({
      query: (accessToken) => ({
        url: '/auth/passkey/login/options',
        method: 'POST',
        data: isCookieAuthenticationEnabled
          ? {}
          : { access_token: accessToken },
      }),
      transformErrorResponse: safePasskeyError,
    }),
    verifyPasskeyLogin: builder.mutation<
      PasskeyRegistrationAuth,
      VerifyPasskeyLoginRequest
    >({
      async queryFn(
        { accessToken, authenticationResponse, challenge },
        _,
        __,
        baseQuery,
      ) {
        const cookieMode = isCookieAuthenticationEnabled
        const data: VerifyAuthenticationDto = cookieMode
          ? {
              returnType: VerifyAuthenticationDtoReturnTypeEnum.COOKIES,
              authenticationResponse,
              challenge,
            }
          : {
              access_token: accessToken,
              accessToken,
              returnType: VerifyAuthenticationDtoReturnTypeEnum.OBJECT,
              authenticationResponse,
              challenge,
            }
        const result = await baseQuery({
          url: '/auth/passkey/login/verify',
          method: 'POST',
          data,
        })
        if (result.error) return { error: safePasskeyError(result.error) }

        const projected = projectPasskeyAuth(result.data, cookieMode)
        if (cookieMode && !projected.userId.trim())
          return { error: { status: 'INVALID_RESPONSE' } }
        return { data: projected }
      },
    }),
    login: builder.mutation<AuthResponse, Omit<SignInDtoV3, 'returnType'>>({
      queryFn: async (queryArgs, _api, _options, baseQuery) => {
        const browserInstallationId = await getOptionalBrowserInstallationId()
        const result = await baseQuery({
          url: '/v3/auth/login',
          method: 'POST',
          data: {
            email: queryArgs.email.trim(),
            password: queryArgs.password,
            ...(browserInstallationId && {
              installationId: browserInstallationId,
            }),
            ...(isCookieAuthenticationEnabled && {
              returnType: SignInDtoV3ReturnTypeEnum.COOKIES,
            }),
          },
        })
        return result.error
          ? { error: result.error }
          : { data: result.data as AuthResponse }
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            recordExplicitLogin(resetOrganizationSwitchSessionAfterLogin()),
          )
          dispatch(
            isTokenAuthResponse(data)
              ? setAuthInfo({
                  userId: data.userId,
                  access_token: data.access_token,
                  refresh_token: data.refresh_token,
                })
              : setAuthInfo({ userId: data.userId }),
          )
        } catch (error) {
          return
        }
      },
    }),
    validateMagicToken: builder.mutation<IOrganizationChangeResponse, string>({
      query: (token) => ({
        url: 'v3/auth/magic-link/login',
        method: 'POST',
        data: {
          magicLinkToken: token,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        if (isCookieAuthenticationEnabled) {
          return
        }

        try {
          const { data } = await queryFulfilled
          dispatch(
            setAuthInfo({
              userId: '',
              access_token: data.access_token,
              refresh_token: '',
            }),
          )
        } catch (error) {
          return
        }
      },
    }),
    forgotPassword: builder.mutation<void, UserEmailDto>({
      query: (queryArgs) => ({
        url: '/v3/auth/forgot-password',
        method: 'POST',
        data: {
          email: queryArgs.email.trim(),
        },
      }),
    }),
    setNewPassword: builder.mutation<{ Ok: true }, SetNewPasswordRequest>({
      query: ({ password, bearerToken }) => ({
        url: '/v3/auth/set-new-password',
        method: 'POST',
        data: {
          password,
        },
        ...(bearerToken && {
          headers: { Authorization: `Bearer ${bearerToken}` },
        }),
      }),
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
        data: {
          installationId: getExistingBrowserInstallationId() ?? undefined,
        },
      }),
    }),
    getOrganizationsByUserId: builder.query<
      ApiResponseSerializer<SessionOrganization>,
      string
    >({
      query: (userId) => ({
        url: `/v3/auth/organizations/${userId}`,
        method: 'GET',
      }),
      transformResponse: (
        response: ApiResponseSerializer<OrganizationDTOV2>,
      ) => ({
        ...response,
        records: prepareUserOrganizations(response.records),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(
            setUserSession({
              organizations: data.records,
            }),
          )
        } catch (error) {
          return
        }
      },
    }),
    changeOrganization: builder.mutation<AuthResponse, RefreshAuthRequest>({
      queryFn: async (data, _api, _options, baseQuery) => {
        const browserInstallationId = await getOptionalBrowserInstallationId()
        const result = await baseQuery({
          url: '/v3/auth/refresh',
          method: 'POST',
          data: {
            organizationId: data.organizationId,
            membershipId: data.membershipId,
            installationId: browserInstallationId,
            switchMembership: true,
            ...(isCookieAuthenticationEnabled
              ? { returnType: RefreshTokenDtoV3ReturnTypeEnum.COOKIES }
              : {
                  refreshToken: data.refreshToken,
                  refresh_token: data.refresh_token,
                }),
          },
        })
        return result.error
          ? { error: result.error }
          : { data: result.data as AuthResponse }
      },
    }),
    getAuthProfile: builder.query<SessionProfile, null>({
      query: () => ({
        url: '/v3/auth/profile',
        method: 'GET',
      }),
      transformResponse: (response: AuthProfileResponse) =>
        toSessionProfile(response),
    }),
    getAuthSessionProfile: builder.query<RestorableSessionProfile, null>({
      query: () => ({
        url: '/v3/auth/profile',
        method: 'GET',
      }),
      transformResponse: (response: AuthSessionProfileResponse) =>
        toRestorableSessionProfile(response),
    }),
    refreshToken: builder.mutation<AuthResponse, RefreshAuthRequest>({
      query: (data) => ({
        url: '/v3/auth/refresh',
        method: 'POST',
        data: {
          installationId: getExistingBrowserInstallationId() ?? undefined,
          ...(isCookieAuthenticationEnabled
            ? { returnType: RefreshTokenDtoV3ReturnTypeEnum.COOKIES }
            : {
                refreshToken: data.refreshToken,
                refresh_token: data.refresh_token,
              }),
          ...(data.organizationId && { organizationId: data.organizationId }),
          ...(data.membershipId && { membershipId: data.membershipId }),
        },
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            setAuthInfo({
              userId: data.userId,
              ...(isTokenAuthResponse(data) && {
                access_token: data.access_token,
                refresh_token: data.refresh_token,
              }),
              ...(args.organizationId && {
                organizationId: args.organizationId,
              }),
              ...(args.membershipId && {
                membershipId: args.membershipId,
              }),
            }),
          )
        } catch (error) {
          return
        }
      },
    }),
  }),
})

export const {
  usePasskeyRegistrationOptionsMutation,
  useVerifyPasskeyRegistrationMutation,
  usePasskeyLoginOptionsMutation,
  useVerifyPasskeyLoginMutation,
  useLoginMutation,
  useForgotPasswordMutation,
  useSetNewPasswordMutation,
  useLogoutMutation,
  useValidateMagicTokenMutation,
  useGetOrganizationsByUserIdQuery,
  useChangeOrganizationMutation,
  useGetAuthProfileQuery,
  useRefreshTokenMutation,
  endpoints: authApiEndpoints,
} = authApi
